import { CursorPaginatedResponseSchema } from 'notable-common';
import { z } from 'zod';
/** Route params for the endpoint addressing a single previsit form template. */
export const PrevisitFormTemplateIdParamsSchema = z
    .object({ previsitFormTemplateId: z.uuid() })
    .strict();
/** One question on a previsit form template, without the rendering detail the patient app needs. */
export const PrevisitFormTemplateFieldSchema = z.object({
    id: z.uuid(),
    name: z.string().describe('The field key answers are stored and exported under.'),
    label: z.string().describe('The question as the patient reads it.'),
    sublabel: z.string().nullable(),
    primitive: z
        .string()
        .describe('The kind of question, for example TEXT, SINGLE_SELECT, or a domain primitive such as PATIENT_INSURANCE.'),
    page: z.number().describe('Which page of the form the question is on, starting at 1.'),
    rank: z.number().describe('Position of the question within its page.'),
    required: z.boolean(),
    options: z
        .array(z.string())
        .nullable()
        .describe('The choices a select question offers, null for a question that is not a select.'),
});
/**
 * How a scored form turns answers into an outcome: the first rule whose conditions hold decides it,
 * and `defaultOutcome` applies when none do. Within a rule, the inner arrays are OR-ed and the
 * checks inside one are AND-ed.
 */
export const PrevisitFormTemplateOutcomeRulesSchema = z.object({
    defaultOutcome: z.string(),
    rules: z.array(z.object({
        outcome: z.string(),
        conditions: z.array(z.array(z.object({
            fieldConfigName: z.string().describe('The `name` of the question this check reads.'),
            matchingValues: z.array(z.string()),
            multiMatch: z.boolean(),
        }))),
    })),
});
/**
 * One previsit form template: the questionnaire itself, as authored, rather than any patient's
 * copy of it. A previsit form set is the copy a patient is asked to fill in; answers live there
 * and never here.
 */
export const PrevisitFormTemplateSchema = z.object({
    id: z.uuid(),
    name: z.string().describe('The template key, stable across practices and organizations.'),
    displayName: z.string().describe('The form title a patient sees.'),
    organizationId: z
        .uuid()
        .nullable()
        .describe('The organization owning the template, or null for one shared across all of them.'),
    version: z.number(),
    labels: z
        .array(z.string())
        .nullable()
        .describe('Labels grouping templates by purpose, for example screeners or consents.'),
    languages: z
        .array(z.string())
        .describe('ISO 639-1 codes for the languages the template is translated into, beyond its authored English.'),
    notificationSchemeId: z
        .uuid()
        .nullable()
        .describe('The outreach program that asks a patient to complete this form, if any.'),
    noteTemplateId: z
        .string()
        .nullable()
        .describe('The note template answers are written back to, if any.'),
    exportAsForm: z.boolean().describe('Whether completed answers export to the EHR as a form.'),
    hideWhenCompleted: z
        .boolean()
        .describe('Whether the patient stops seeing this form in their previsit paperwork once they finish it.'),
    deleted: z
        .boolean()
        .describe('Whether the template has been retired. A retired template stays readable because form sets already made from it still reference it, but no new set uses it.'),
    deletedTimestamp: z.iso.datetime().nullable().describe('When the template was retired.'),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
    outcomeRules: PrevisitFormTemplateOutcomeRulesSchema.nullable().describe('How a scored form, such as a screener, turns answers into an outcome. Null when the form is not scored.'),
    fields: z
        .array(PrevisitFormTemplateFieldSchema)
        .describe('Every question authored on the template, ordered by page and then by rank. This is not the ' +
        'set of questions a given patient is asked: a question can be conditional on an earlier ' +
        'answer, and those conditions are not included here, so patient-visible branching cannot ' +
        'be inferred from this list.'),
});
export const PrevisitFormTemplateListEntrySchema = PrevisitFormTemplateSchema.pick({
    id: true,
    name: true,
    displayName: true,
    labels: true,
    created: true,
    updated: true,
}).extend({
    organizationId: z.uuid().describe('The organization owning the template.'),
});
/** One page of templates ordered by name, in the envelope every cursor-paginated route returns. */
export const PrevisitFormTemplateListResponseSchema = CursorPaginatedResponseSchema(PrevisitFormTemplateListEntrySchema);
//# sourceMappingURL=templateResponse.js.map